import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "./api";

/**
 * Public nursery settings (name + logo) exposed everywhere the app shell renders.
 * Fetches /api/public-settings once on mount and again whenever refresh() is
 * called (e.g. after the admin saves Settings). The logo URL is cache-busted
 * with settings.updated_at so a new upload appears without a hard refresh.
 */
const SettingsContext = createContext({
  nurseryName: "DocBlock Wholesale",
  logoUrl: null,
  updatedAt: null,
  loading: true,
  refresh: () => {},
});

export function SettingsProvider({ children }) {
  const [state, setState] = useState({
    nurseryName: "DocBlock Wholesale",
    logoStoragePath: null,
    updatedAt: null,
    loading: true,
  });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/public-settings");
      setState({
        nurseryName: data.nursery_name || "DocBlock Wholesale",
        logoStoragePath: data.logo_storage_path || null,
        updatedAt: data.updated_at || null,
        loading: false,
      });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const value = useMemo(() => {
    const bust = state.updatedAt ? `?v=${encodeURIComponent(state.updatedAt)}` : "";
    return {
      nurseryName: state.nurseryName,
      logoUrl: state.logoStoragePath ? `${fileUrl(state.logoStoragePath)}${bust}` : null,
      updatedAt: state.updatedAt,
      loading: state.loading,
      refresh: load,
    };
  }, [state, load]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useNurserySettings() {
  return useContext(SettingsContext);
}
